'use client';

import React, { useState } from 'react';
import { useAuthStore } from '@/stores/auth-store';
import { useAppStore } from '@/stores/app-store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export function LoginScreen() {
  const { login } = useAuthStore();
  const { setCurrentView, isOnline } = useAppStore();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!username.trim() || !password.trim()) {
      setError('कृपया यूजरनेम और पासवर्ड दर्ज करें');
      return;
    }

    setIsLoading(true);

    try {
      const success = await login(username.trim(), password);

      if (success) {
        const { role } = useAuthStore.getState();
        setCurrentView(role === 'admin' ? 'admin-dashboard' : 'officer-dashboard');
      } else {
        if (!navigator.onLine) {
          setError('ऑफलाइन लॉगिन विफल। कृपया सही क्रेडेंशियल दर्ज करें या ऑनलाइन होकर लॉगिन करें।');
        } else {
          setError('अमान्य यूजरनेम या पासवर्ड');
        }
      }
    } catch {
      if (!navigator.onLine) {
        setError('ऑफलाइन लॉगिन विफल। कृपया ऑनलाइन होकर पहले बार लॉगिन करें।');
      } else {
        setError('लॉगिन में त्रुटि हुई। कृपया पुनः प्रयास करें।');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-emerald-100 to-amber-50 p-4">
      {/* Subtle decorative background pattern */}
      <div className="fixed inset-0 opacity-[0.03] pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, #065f46 1px, transparent 0)`,
            backgroundSize: '40px 40px',
          }}
        />
      </div>

      {/* Main login card with fade-in animation */}
      <div
        className="w-full max-w-md animate-in fade-in duration-700"
        style={{
          animation: 'fadeSlideUp 0.7s ease-out',
        }}
      >
        <div className="bg-white rounded-2xl shadow-2xl border border-emerald-100 overflow-hidden">
          {/* Top decorative bar */}
          <div className="h-2 bg-gradient-to-r from-emerald-700 via-emerald-600 to-amber-500" />

          <div className="px-6 pt-8 pb-6 sm:px-8 sm:pt-10">
            {/* Official emblem/logo badge */}
            <div className="flex justify-center mb-5">
              <div className="relative">
                <div className="w-24 h-24 rounded-full bg-white flex items-center justify-center shadow-lg border-4 border-amber-400 overflow-hidden">
                  <img
                    src="/mpsec-logo.png"
                    alt="म.प्र. राज्य निर्वाचन आयोग"
                    className="w-full h-full object-contain"
                  />
                </div>
                {/* Decorative ring */}
                <div className="absolute inset-0 w-24 h-24 rounded-full border-2 border-amber-400/30 scale-110" />
              </div>
            </div>

            {/* Title section */}
            <div className="text-center mb-6">
              <h1 className="text-xl sm:text-2xl font-bold text-emerald-900 mb-1">
                म.प्र. राज्य निर्वाचन आयोग
              </h1>
              <h2 className="text-base sm:text-lg font-semibold text-emerald-700 mb-1">
                Voter Register - मतदाता रजिस्टर
              </h2>
              <p className="text-sm text-emerald-600 font-medium">
                स्थानीय निकाय चुनाव
              </p>
            </div>

            {/* Separator */}
            <div className="flex items-center gap-3 mb-6">
              <div className="flex-1 h-px bg-emerald-200" />
              <div className="w-2 h-2 rounded-full bg-amber-400" />
              <div className="flex-1 h-px bg-emerald-200" />
            </div>

            {/* Login form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Offline mode notice */}
              {!isOnline && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-center gap-2">
                  <svg
                    className="w-5 h-5 text-amber-500 shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M18.364 5.636a9 9 0 010 12.728m-2.829-2.829a5 5 0 000-7.07m-4.243 4.243a1.5 1.5 0 010-2.122"
                    />
                  </svg>
                  <p className="text-sm text-amber-700 font-medium">
                    ऑफलाइन मोड - पहले लॉगिन किए गए खाते ही उपलब्ध हैं
                  </p>
                </div>
              )}

              {/* Error message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-center gap-2 animate-in slide-in-from-top-1 duration-300">
                  <svg
                    className="w-5 h-5 text-red-500 shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <p className="text-sm text-red-700 font-medium">{error}</p>
                </div>
              )}

              {/* Username field */}
              <div className="space-y-2">
                <Label
                  htmlFor="username"
                  className="text-sm font-medium text-gray-600"
                >
                  यूजरनेम <span className="text-gray-400 font-normal">(Username)</span>
                </Label>
                <Input
                  id="username"
                  type="text"
                  placeholder="अपना यूजरनेम दर्ज करें"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={isLoading}
                  autoComplete="username"
                  className="h-11 border-emerald-200 focus-visible:border-emerald-500 focus-visible:ring-emerald-500/20 placeholder:text-gray-400"
                />
              </div>

              {/* Password field */}
              <div className="space-y-2">
                <Label
                  htmlFor="password"
                  className="text-sm font-medium text-gray-600"
                >
                  पासवर्ड <span className="text-gray-400 font-normal">(Password)</span>
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="अपना पासवर्ड दर्ज करें"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                  autoComplete="current-password"
                  className="h-11 border-emerald-200 focus-visible:border-emerald-500 focus-visible:ring-emerald-500/20 placeholder:text-gray-400"
                />
              </div>

              {/* Login button */}
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full h-11 bg-emerald-700 hover:bg-emerald-800 text-white font-semibold text-base shadow-md transition-all duration-200 mt-2"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    लॉगिन हो रहा है...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
                      />
                    </svg>
                    लॉगिन करें
                  </span>
                )}
              </Button>
            </form>
          </div>

          {/* Footer section */}
          <div className="bg-gradient-to-r from-emerald-50 to-emerald-100/50 border-t border-emerald-100 px-6 py-4 sm:px-8">
            <p className="text-center text-xs text-emerald-700 font-medium">
              © म.प्र. राज्य निर्वाचन आयोग
            </p>
            <p className="text-center text-[10px] text-emerald-500 mt-1">
              सुरक्षित प्रणाली | सत्र 2025-26
            </p>
          </div>
        </div>
      </div>

      {/* CSS animation keyframes */}
      <style jsx>{`
        @keyframes fadeSlideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}
